import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Form, Button } from 'react-bootstrap';
import { Trash, Telephone, Calendar, GeoAlt, ChevronUp, ChevronDown, Shop } from 'react-bootstrap-icons';
import { AdminCard } from '../shared/AdminCard';
import { StatusBadge } from '../shared/StatusBadge';
import { ConvertLeadModal } from './ConvertLeadModal';

// Transform API response to component format
function transformLead(apiLead) {
  return {
    id: apiLead.id,
    firstName: apiLead.firstName,
    lastName: apiLead.lastName,
    phone: apiLead.phone,
    state: apiLead.state,
    createdAt: apiLead.createdAt,
    contacted: apiLead.contacted || false,
    injuries: apiLead.injuries || []
  };
}

export function LeadDashboard({ idToken }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const fetchData = useCallback(async () => {
    if (!idToken) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CORE_API}/lead?status=Inquiry`,
        { headers: { Authorization: idToken } }
      );
      const apiLeads = response.data.value || response.data || [];
      setData(apiLeads.map(transformLead));
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCheckboxChange = async (lead) => {
    const newContacted = !lead.contacted;
    try {
      await axios.put(
        `${process.env.REACT_APP_CORE_API}/lead/${lead.id}`,
        { contacted: newContacted },
        { headers: { Authorization: idToken } }
      );
      setData(prevData =>
        prevData.map(item =>
          item.id === lead.id ? { ...item, contacted: newContacted } : item
        )
      );
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const [converting, setConverting] = useState(null);

  const handleConverted = (id) => {
    setData(prev => prev.filter(item => item.id !== id));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_CORE_API}/lead/${id}`,
        { headers: { Authorization: idToken } }
      );
      setData(prevData => prevData.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sortable header component
  const SortableHeader = ({ label, sortKey }) => (
    <th
      onClick={() => handleSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {label}
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        )}
      </span>
    </th>
  );

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = data.filter(item => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        item.firstName?.toLowerCase().includes(searchLower) ||
        item.lastName?.toLowerCase().includes(searchLower) ||
        item.phone?.toLowerCase().includes(searchLower) ||
        item.state?.toLowerCase().includes(searchLower)
      );
    });

    // Sort
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let aVal, bVal;

        if (sortConfig.key === 'name') {
          aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        } else if (sortConfig.key === 'status') {
          aVal = a.contacted ? 1 : 0;
          bVal = b.contacted ? 1 : 0;
        } else if (sortConfig.key === 'createdAt') {
          // Parse dates for proper sorting
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        } else {
          aVal = (a[sortConfig.key] || '').toString().toLowerCase();
          bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, sortConfig]);

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Quote Requests</h1>
        <p className="admin-page-description">
          People who requested injury estimates through the quote tool
        </p>
      </div>

      <AdminCard noPadding>
        {/* Search */}
        <div style={{ padding: 'var(--space-4)' }}>
          <Form.Control
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Desktop Table */}
        <div className="admin-table-wrapper admin-table-desktop">
          <table className="admin-table">
            <thead>
              <tr>
                <SortableHeader label="Name" sortKey="name" />
                <SortableHeader label="Phone" sortKey="phone" />
                <SortableHeader label="State" sortKey="state" />
                <SortableHeader label="Date" sortKey="createdAt" />
                <SortableHeader label="Contacted" sortKey="status" />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="admin-empty-state">
                      <p className="admin-empty-description">No leads found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <strong>{lead.firstName} {lead.lastName}</strong>
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Telephone size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {lead.phone}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <GeoAlt size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {lead.state}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Form.Check
                          type="checkbox"
                          checked={lead.contacted}
                          onChange={() => handleCheckboxChange(lead)}
                          style={{ cursor: 'pointer' }}
                        />
                        <StatusBadge status={lead.contacted ? 'contacted' : 'new'} />
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          className="admin-btn-icon"
                          onClick={() => setConverting(lead)}
                          title="Post to marketplace"
                        >
                          <Shop size={16} />
                        </button>
                        <button
                          className="admin-btn-icon danger"
                          onClick={() => handleDelete(lead.id)}
                          title="Delete lead"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="admin-mobile-cards" style={{ padding: 'var(--space-4)' }}>
          {filteredData.length === 0 ? (
            <div className="admin-empty-state">
              <p className="admin-empty-description">No leads found</p>
            </div>
          ) : (
            filteredData.map((lead) => (
              <div key={lead.id} className="admin-mobile-card">
                <div className="admin-mobile-card-header">
                  <span className="admin-mobile-card-title">
                    {lead.firstName} {lead.lastName}
                  </span>
                  <StatusBadge status={lead.contacted ? 'contacted' : 'new'} />
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">Phone</span>
                  <span className="admin-mobile-card-value">{lead.phone}</span>
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">State</span>
                  <span className="admin-mobile-card-value">{lead.state}</span>
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">Date</span>
                  <span className="admin-mobile-card-value">
                    {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="admin-mobile-card-actions">
                  <Button
                    variant={lead.contacted ? 'success' : 'outline-primary'}
                    size="sm"
                    onClick={() => handleCheckboxChange(lead)}
                  >
                    {lead.contacted ? 'Contacted' : 'Mark Contacted'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setConverting(lead)}
                  >
                    <Shop size={14} className="me-1" />Post
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(lead.id)}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </AdminCard>

      <ConvertLeadModal
        lead={converting}
        show={!!converting}
        onHide={() => setConverting(null)}
        idToken={idToken}
        onConverted={handleConverted}
      />
    </div>
  );
}
